<script lang="ts">
  import { buildTableRowActionSet } from "@kontextwerk/web-sdk/core/layout/table"

  import type { AppointmentPlanningStartBound } from "../../../../_shared/_logic/start-bound"
  import Table, {
    type ColumnDef,
    type TableGetRows,
    type TableRowsUpdater,
  } from "@kontextwerk/web-sdk/ui/layout/Table.svelte"
  import type { TableSelectionChangeDetail } from "@kontextwerk/web-sdk/core/layout/table"
  import type { Snippet } from "svelte"
  import { STABLE_ROW_NUMBER_COLUMN_ID } from "../../../../../../_logic/stable-row-numbers"
  import type { BatchTableSelectionChangeDetail } from "../../../../../../_logic/batch-apply-targets"
  import EditContactAvailabilityAction from "./_actions/EditContactAvailabilityAction.actions"
  import AppointmentCell from "./_modules/appointment-cell"
  import AppointmentEmployeeCell from "./_modules/appointment-employee-cell"
  import AppointmentTypeCell from "./_modules/appointment-type-cell"
  import DurationCell from "./_modules/duration-cell"
  import OrderTypeCell from "./_modules/order-type-cell"
  import PlanningStrategyCell from "./_modules/planning-strategy-cell"
  import PropertyCell from "./_modules/property-cell"
  import RouteAnchorCell from "./_modules/route-anchor-cell"
  import WarningCell from "./_modules/warning-cell"
  import type {
    PlanningPreviewRow,
    PlanningPreviewRowChange,
    PlanningTableRowControlsResolver,
    PlanningTableRowStateResolver,
    PreviewTableRow,
  } from "../../../../_shared/_logic/table-contract"
  import type { AppointmentPlanningTableSortState } from "../../_shared/_logic/filters"

  type Props = {
    startBound: AppointmentPlanningStartBound
    columns: ColumnDef[]
    getRows: TableGetRows
    refreshKey: string
    defaultSort: AppointmentPlanningTableSortState
    pageSize: number
    showActions?: boolean
    rowControls?: PlanningTableRowControlsResolver
    getRowState?: PlanningTableRowStateResolver
    enableSelection?: boolean
    selectedIds?: string[]
    batchActions?: Snippet<[TableSelectionChangeDetail]>
    disabled?: boolean
    onSelectionChange?: (detail: BatchTableSelectionChangeDetail) => void
    onRowsUpdaterReady?: (updater: TableRowsUpdater) => void
    onRowChange: (row: PlanningPreviewRow, change: PlanningPreviewRowChange) => void
    onEditContactAvailability?: (row: PlanningPreviewRow) => Promise<void> | void
  }

  const {
    startBound,
    columns,
    getRows,
    refreshKey,
    defaultSort,
    pageSize,
    showActions = false,
    rowControls = undefined,
    getRowState = undefined,
    enableSelection = false,
    selectedIds = [],
    batchActions = undefined,
    disabled = false,
    onSelectionChange = undefined,
    onRowsUpdaterReady = undefined,
    onRowChange,
    onEditContactAvailability = undefined,
  }: Props = $props()
</script>

<Table
  rowActions={{
    resolve: (row, rowIndex) =>
      buildTableRowActionSet(
        ((row) => {
          const actions: import("@kontextwerk/web-sdk/core/layout/table").TableRowAction[] = []

          const preview = row as PreviewTableRow

          if ("isLoading" in preview) {
          } else {
            if (onEditContactAvailability) {
              actions.push(
                ...EditContactAvailabilityAction({
                  row: preview,
                  disabled: disabled || preview.removed || preview.committed,
                  onEdit: onEditContactAvailability,
                }),
              )
            }
          }

          return actions
        })(row) ?? [],
        { primaryId: "action-0", inlineIds: [] },
      ),
  }}
  {columns}
  {getRows}
  {refreshKey}
  {defaultSort}
  {pageSize}
  theme="light"
  mode="table-scroll"
  {showActions}
  {rowControls}
  {getRowState}
  actionsHeaderLabel="Aktionen"
  {enableSelection}
  {selectedIds}
  {batchActions}
  batchActionsSurfaceTone="dark"
  {onSelectionChange}
  setRowsUpdater={onRowsUpdaterReady}
>
  {#snippet empty()}
    <div class="empty-row">Noch keine Einträge aus der Liste ausgewählt.</div>
  {/snippet}

  {#snippet tableCell({ column, row })}
    {@const preview = row as PreviewTableRow}
    {#if "isLoading" in preview}
      <div
        class:wide={column.id === "property"}
        class="loading-cell"
        aria-label="Ergebnisvorschau wird geladen"
      >
        <span class="loading-placeholder" class:tall={column.id === "warning"}></span>
      </div>
    {:else if column.id === STABLE_ROW_NUMBER_COLUMN_ID}
      {preview[STABLE_ROW_NUMBER_COLUMN_ID]}
    {:else if column.id === "property"}
      <PropertyCell row={preview} />
    {:else if column.id === "orderType"}
      <OrderTypeCell row={preview} disabled={disabled || preview.removed || preview.committed} />
    {:else if column.id === "duration"}
      <DurationCell row={preview} />
    {:else if column.id === "appointmentEmployee"}
      <AppointmentEmployeeCell
        row={preview}
        disabled={disabled || preview.removed || preview.committed}
        onChange={onRowChange}
      />
    {:else if column.id === "planningStrategy"}
      <PlanningStrategyCell
        row={preview}
        disabled={disabled || preview.removed || preview.committed}
        onChange={onRowChange}
      />
    {:else if column.id === "appointmentType"}
      <AppointmentTypeCell
        row={preview}
        disabled={disabled || preview.removed || preview.committed}
        onChange={onRowChange}
      />
    {:else if column.id === "appointment"}
      <AppointmentCell
        {startBound}
        row={preview}
        disabled={disabled || preview.removed || preview.committed}
        onChange={onRowChange}
      />
    {:else if column.id === "warning"}
      <WarningCell row={preview} />
    {:else if column.id === "arrival"}
      <RouteAnchorCell
        anchor={preview.arrivalAnchor}
        emptyLabel="Keine Anfahrt"
        emptyTooltip="Vor diesem Terminvorschlag gibt es keinen bekannten Kontextanker."
      />
    {:else if column.id === "departure"}
      <RouteAnchorCell
        anchor={preview.departureAnchor}
        emptyLabel="Keine Abfahrt"
        emptyTooltip="Nach diesem Terminvorschlag gibt es keinen bekannten Kontextanker."
      />
    {/if}
  {/snippet}
</Table>

<style lang="less">
  .empty-row {
    padding: 0.4rem 0;
    color: color-mix(in srgb, var(--text-100) 70%, transparent);
  }

  .loading-cell {
    width: min(100%, 11rem);
    padding: 0.2rem 0;

    &.wide {
      width: min(100%, 16rem);
    }
  }

  .loading-placeholder {
    display: block;
    width: 100%;
    height: 0.8rem;
    border-radius: 0;
    background: linear-gradient(
      90deg,
      color-mix(in srgb, var(--text-100) 7%, transparent),
      color-mix(in srgb, var(--text-100) 14%, transparent),
      color-mix(in srgb, var(--text-100) 7%, transparent)
    );

    &.tall {
      height: 1.4rem;
    }
  }

  :global(.appointment-planning-table-input) {
    width: 100%;
    min-width: 0;
  }
</style>
